// File: frontend/src/components/Users/UsersStats.jsx
// Users statistics component

import React from 'react';
import { UserGroupIcon, ShieldCheckIcon, EyeSlashIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const UsersStats = ({ users, roles }) => {
  // Calculate stats
  const totalUsers = users.length;
  const activeUsers = users.filter(user => user.is_active).length;
  const inactiveUsers = totalUsers - activeUsers;
  
  // Count users by role
  const roleStats = roles.map(role => ({
    ...role,
    count: users.filter(user => user.role_name === role.name).length
  }));

  const stats = [
    {
      name: 'Total de Usuários',
      value: totalUsers,
      icon: UserGroupIcon,
      color: 'blue',
      description: 'Usuários cadastrados no sistema'
    },
    {
      name: 'Usuários Ativos',
      value: activeUsers,
      icon: CheckCircleIcon,
      color: 'green',
      description: 'Usuários com acesso ativo'
    },
    {
      name: 'Usuários Inativos',
      value: inactiveUsers,
      icon: EyeSlashIcon,
      color: 'red',
      description: 'Usuários com acesso bloqueado'
    },
    {
      name: 'Perfis de Acesso',
      value: roles.length,
      icon: ShieldCheckIcon,
      color: 'purple',
      description: 'Tipos de perfil disponíveis'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-50 text-blue-600 border-blue-200',
      green: 'bg-green-50 text-green-600 border-green-200',
      red: 'bg-red-50 text-red-600 border-red-200',
      purple: 'bg-purple-50 text-purple-600 border-purple-200'
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="space-y-6">
      {/* Main Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div
              key={stat.name}
              className={`rounded-lg border p-6 ${getColorClasses(stat.color)}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Icon className="h-8 w-8 mr-3" />
                  <div>
                    <p className="text-sm font-medium opacity-75">{stat.name}</p>
                    <p className="text-2xl font-bold">{stat.value}</p>
                  </div>
                </div>
              </div>
              <p className="text-xs opacity-60 mt-2">{stat.description}</p>
            </div>
          );
        })}
      </div>

      {/* Role Distribution */}
      {roleStats.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Distribuição por Perfil de Acesso
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {roleStats.map((role) => (
              <div
                key={role.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
              >
                <div>
                  <h4 className="font-medium text-gray-900">{role.name}</h4>
                  <p className="text-sm text-gray-500 truncate max-w-48">
                    {role.description}
                  </p>
                </div>
                <div className="text-right">
                  <span className="text-2xl font-bold text-gray-900">
                    {role.count}
                  </span>
                  <p className="text-xs text-gray-500">usuários</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-blue-900">Ações Rápidas</h3>
            <p className="text-blue-600 text-sm">
              Gerencie usuários e permissões de forma eficiente
            </p>
          </div>
          <div className="flex space-x-3">
            <button className="inline-flex items-center px-3 py-2 border border-blue-300 rounded-md text-sm font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 transition-colors">
              <ShieldCheckIcon className="h-4 w-4 mr-2" />
              Gerenciar Perfis
            </button>
            <button className="inline-flex items-center px-3 py-2 border border-green-300 rounded-md text-sm font-medium text-green-700 bg-green-100 hover:bg-green-200 transition-colors">
              <UserGroupIcon className="h-4 w-4 mr-2" />
              Relatório de Usuários
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsersStats;